import { gatewayAuthHeaders } from './gateway-auth';
import type {
  SubnetConfig,
  SiteGateway,
  SiteGatewayWithSite,
  ProvisioningResult,
  RscGenerationResult,
  SubnetSize,
  AccountGateway,
  AccountGatewayWithAccount,
  AccountGatewayProvisioningResult,
  RouterCredentials,
} from '@asi/shared';

const GATEWAY_URL = import.meta.env.VITE_GATEWAY_URL as string | undefined;

async function callProvisioningApi<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  if (!GATEWAY_URL) {
    throw new Error('Gateway URL not configured');
  }

  const url = `${GATEWAY_URL}/api/provisioning${path}`;
  const init: RequestInit = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...gatewayAuthHeaders(),
      ...options.headers,
    },
  };

  const res = await fetch(url, init);
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as Record<string, string>;
    throw new Error(err.error ?? `Provisioning error ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// ─── API Functions ──────────────────────────────────────────────

export async function fetchSubnetSizes(): Promise<SubnetConfig[]> {
  const data = await callProvisioningApi<{ sizes: SubnetConfig[] }>('/sizes');
  return data.sizes;
}

export async function fetchAllGateways(): Promise<SiteGatewayWithSite[]> {
  const data = await callProvisioningApi<{ gateways: SiteGatewayWithSite[] }>('/gateways');
  return data.gateways;
}

export async function fetchGatewayForSite(siteId: string): Promise<SiteGateway | null> {
  const data = await callProvisioningApi<{ gateway: SiteGateway | null }>(`/gateways/${siteId}`);
  return data.gateway;
}

export async function provisionGateway(
  siteId: string,
  size: SubnetSize = 'S',
): Promise<ProvisioningResult> {
  return callProvisioningApi<ProvisioningResult>('/provision', {
    method: 'POST',
    body: JSON.stringify({ siteId, size }),
  });
}

export async function generateRsc(siteId: string, includeWifi = false, includeAlarmPanel = false): Promise<RscGenerationResult> {
  return callProvisioningApi<RscGenerationResult>('/generate-rsc', {
    method: 'POST',
    body: JSON.stringify({ siteId, includeWifi, includeAlarmPanel }),
  });
}

export async function deprovisionGateway(siteId: string): Promise<void> {
  await callProvisioningApi(`/gateways/${siteId}`, { method: 'DELETE' });
}

export async function resizeGateway(
  siteId: string,
  size: SubnetSize,
): Promise<{ gateway: SiteGateway; previousSubnet: string; newSubnet: string }> {
  return callProvisioningApi('/resize', {
    method: 'PUT',
    body: JSON.stringify({ siteId, size }),
  });
}

/** Download .rsc file as a blob */
export async function downloadRscFile(siteId: string, includeWifi = false, includeAlarmPanel = false): Promise<void> {
  const result = await generateRsc(siteId, includeWifi, includeAlarmPanel);
  const blob = new Blob([result.rsc], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = result.filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Configure EC2 WireGuard peer automatically */
export async function configureEc2Peer(siteId: string): Promise<{ success: boolean; alreadyConfigured?: boolean }> {
  return callProvisioningApi('/configure-ec2-peer', {
    method: 'POST',
    body: JSON.stringify({ siteId }),
  });
}

// ═══════════════════════════════════════════════════════════════
// ACCOUNT GATEWAYS — one gateway per account (new architecture)
// ═══════════════════════════════════════════════════════════════

export async function fetchAllAccountGateways(): Promise<AccountGatewayWithAccount[]> {
  const data = await callProvisioningApi<{ gateways: AccountGatewayWithAccount[] }>('/account/gateways');
  return data.gateways;
}

export async function fetchAccountGateway(accountId: string): Promise<AccountGateway | null> {
  const data = await callProvisioningApi<{ gateway: AccountGateway | null }>(
    `/account/gateways/${accountId}`,
  );
  return data.gateway;
}

export async function provisionAccountGateway(
  accountId: string,
  size: SubnetSize = 'S',
): Promise<AccountGatewayProvisioningResult> {
  return callProvisioningApi<AccountGatewayProvisioningResult>('/account/provision', {
    method: 'POST',
    body: JSON.stringify({ accountId, size }),
  });
}

export async function generateAccountRsc(accountId: string, includeWifi = false, includeAlarmPanel = false): Promise<RscGenerationResult> {
  return callProvisioningApi<RscGenerationResult>('/account/generate-rsc', {
    method: 'POST',
    body: JSON.stringify({ accountId, includeWifi, includeAlarmPanel }),
  });
}

export async function downloadAccountRscFile(accountId: string, includeWifi = false, includeAlarmPanel = false): Promise<void> {
  const result = await generateAccountRsc(accountId, includeWifi, includeAlarmPanel);
  const blob = new Blob([result.rsc], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = result.filename;
  a.click();
  URL.revokeObjectURL(url);
}

export async function configureAccountEc2Peer(
  accountId: string,
): Promise<{ success: boolean; alreadyConfigured?: boolean }> {
  return callProvisioningApi('/account/configure-ec2-peer', {
    method: 'POST',
    body: JSON.stringify({ accountId }),
  });
}

export async function deprovisionAccountGateway(accountId: string): Promise<void> {
  await callProvisioningApi(`/account/gateways/${accountId}`, { method: 'DELETE' });
}

// ═══════════════════════════════════════════════════════════════
// ROUTER CREDENTIALS — derived per router, every lookup is audited
// ═══════════════════════════════════════════════════════════════

/** `code` accepts ASI-XXXXXXXX, the identity shown in WinBox, or the gateway row UUID. */
export async function fetchRouterCredentials(code: string): Promise<RouterCredentials> {
  return callProvisioningApi<RouterCredentials>(`/router-credentials/${encodeURIComponent(code.trim())}`);
}

